import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export const STAY_ENTITLEMENT_RELATED_ROW_BUDGET = 100;
export interface StayEntitlementIssue {
  command_id: string;
  entitlement_id: string;
  resource_id: string | null;
  claim_id: string;
  resource_purpose: string;
  stay_status: string;
  user_status: string;
  guest_identity_status: string;
  source_revision: number;
  invitation_revision: number;
  ownership_epoch: number;
  claim_generation: number;
  credential_generation: number;
  aggregate_revision: number;
  valid_from: string;
  valid_until: string;
  checked_at: string;
  requested_at: string;
  status: string;
  result_mode: string | null;
  result_receipt_ref: string | null;
  result_observed_at: string | null;
}
export interface StayEntitlementRevocation {
  command_id: string;
  receipt_id: string | null;
  status: string;
  delivery_state: string;
  credential_generation: number;
  original_credential_generation: number;
  aggregate_revision: number;
  requested_at: string;
  outcome: string | null;
  observed_at: string | null;
  operator_action_required: boolean;
  automatic_physical_revocation_allowed: boolean;
  batch_scope_status: string | null;
  batch_scope_receipt_reference: string | null;
}
export interface StayEntitlementGrant {
  entitlement_id: string;
  resource_id: string | null;
  online_issue_command_id?: string | null;
  claim_id: string;
  ownership_epoch: number;
  claim_generation: number;
  credential_generation: number;
  aggregate_revision: number;
  issued_at: string;
  status: string;
  revocation: StayEntitlementRevocation | null;
}
export interface StayEntitlementAccessGroup {
  group_id: string;
  shared_slot_count: number;
}
export interface StayEntitlementAccessMapping {
  scope: 'CORE_LORA';
  status: 'OBSERVED' | 'UNAVAILABLE';
  property_id: string | null;
  room_id: string | null;
  slot_id: string;
  topology_revision: number | null;
  access_revision: number | null;
  groups: StayEntitlementAccessGroup[];
  online_clue_authorization: 'NOT_INCLUDED';
}
export interface StayEntitlements {
  stay_id: string;
  issues: StayEntitlementIssue[];
  grants: StayEntitlementGrant[];
  access_mapping: StayEntitlementAccessMapping;
}
type ObjectRecord = Record<string, unknown>;
const record = (value: unknown): value is ObjectRecord =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const nullableText = (value: unknown): value is string | null => value === null || text(value);
const integer = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
const nullableUuid = (value: unknown): value is string | null => value === null || isUuid(value);
const nullableDate = (value: unknown): value is string | null =>
  value === null || isOffsetDateTime(value);
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '출입 자격 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
function exact(value: ObjectRecord, keys: readonly string[]): boolean {
  return Object.keys(value).length === keys.length && keys.every((key) => key in value);
}
function exactWithOptional(
  value: ObjectRecord,
  requiredKeys: readonly string[],
  optionalKeys: readonly string[],
): boolean {
  const keys = Object.keys(value);
  return (
    requiredKeys.every((key) => key in value) &&
    keys.every((key) => requiredKeys.includes(key) || optionalKeys.includes(key))
  );
}
const issueKeys = [
  'command_id',
  'entitlement_id',
  'resource_id',
  'claim_id',
  'resource_purpose',
  'stay_status',
  'user_status',
  'guest_identity_status',
  'source_revision',
  'invitation_revision',
  'ownership_epoch',
  'claim_generation',
  'credential_generation',
  'aggregate_revision',
  'valid_from',
  'valid_until',
  'checked_at',
  'requested_at',
  'status',
  'result_mode',
  'result_receipt_ref',
  'result_observed_at',
] as const;
const revocationKeys = [
  'command_id',
  'receipt_id',
  'status',
  'delivery_state',
  'credential_generation',
  'original_credential_generation',
  'aggregate_revision',
  'requested_at',
  'outcome',
  'observed_at',
  'operator_action_required',
  'automatic_physical_revocation_allowed',
  'batch_scope_status',
  'batch_scope_receipt_reference',
] as const;
const grantKeys = [
  'entitlement_id',
  'resource_id',
  'claim_id',
  'ownership_epoch',
  'claim_generation',
  'credential_generation',
  'aggregate_revision',
  'issued_at',
  'status',
  'revocation',
] as const;
const onlineIssueCommandIdKey = 'online_issue_command_id';

export function parseIssue(value: unknown): StayEntitlementIssue {
  if (
    !record(value) ||
    !exact(value, issueKeys) ||
    !isUuid(value.command_id) ||
    !isUuid(value.entitlement_id) ||
    !nullableUuid(value.resource_id) ||
    !isUuid(value.claim_id) ||
    !text(value.resource_purpose) ||
    !text(value.stay_status) ||
    !text(value.user_status) ||
    !text(value.guest_identity_status) ||
    !integer(value.source_revision) ||
    !integer(value.invitation_revision) ||
    !integer(value.ownership_epoch) ||
    !integer(value.claim_generation) ||
    !integer(value.credential_generation) ||
    !integer(value.aggregate_revision) ||
    !isOffsetDateTime(value.valid_from) ||
    !isOffsetDateTime(value.valid_until) ||
    !isOffsetDateTime(value.checked_at) ||
    !isOffsetDateTime(value.requested_at) ||
    !text(value.status) ||
    !nullableText(value.result_mode) ||
    !nullableText(value.result_receipt_ref) ||
    !nullableDate(value.result_observed_at)
  )
    throw invalid();
  return value as unknown as StayEntitlementIssue;
}

function parseRevocation(value: unknown): StayEntitlementRevocation {
  if (
    !record(value) ||
    !exact(value, revocationKeys) ||
    !isUuid(value.command_id) ||
    !nullableUuid(value.receipt_id) ||
    !text(value.status) ||
    !text(value.delivery_state) ||
    !integer(value.credential_generation) ||
    !integer(value.original_credential_generation) ||
    !integer(value.aggregate_revision) ||
    !isOffsetDateTime(value.requested_at) ||
    !nullableText(value.outcome) ||
    !nullableDate(value.observed_at) ||
    typeof value.operator_action_required !== 'boolean' ||
    typeof value.automatic_physical_revocation_allowed !== 'boolean' ||
    !nullableText(value.batch_scope_status) ||
    !nullableUuid(value.batch_scope_receipt_reference)
  )
    throw invalid();
  return value as unknown as StayEntitlementRevocation;
}

export function parseGrant(value: unknown): StayEntitlementGrant {
  if (
    !record(value) ||
    !exactWithOptional(value, grantKeys, [onlineIssueCommandIdKey]) ||
    !isUuid(value.entitlement_id) ||
    !nullableUuid(value.resource_id) ||
    (onlineIssueCommandIdKey in value && !nullableUuid(value[onlineIssueCommandIdKey])) ||
    !isUuid(value.claim_id) ||
    !integer(value.ownership_epoch) ||
    !integer(value.claim_generation) ||
    !integer(value.credential_generation) ||
    !integer(value.aggregate_revision) ||
    !isOffsetDateTime(value.issued_at) ||
    !text(value.status) ||
    !(value.revocation === null || record(value.revocation))
  )
    throw invalid();
  return {
    entitlement_id: value.entitlement_id,
    resource_id: value.resource_id,
    ...(onlineIssueCommandIdKey in value
      ? { online_issue_command_id: value[onlineIssueCommandIdKey] as string | null }
      : {}),
    claim_id: value.claim_id,
    ownership_epoch: value.ownership_epoch,
    claim_generation: value.claim_generation,
    credential_generation: value.credential_generation,
    aggregate_revision: value.aggregate_revision,
    issued_at: value.issued_at,
    status: value.status,
    revocation: value.revocation === null ? null : parseRevocation(value.revocation),
  };
}

function parseAccessGroup(value: unknown): StayEntitlementAccessGroup {
  if (
    !record(value) ||
    !exact(value, ['group_id', 'shared_slot_count']) ||
    !isUuid(value.group_id) ||
    !integer(value.shared_slot_count)
  )
    throw invalid();
  return value as unknown as StayEntitlementAccessGroup;
}

function parseAccessMapping(value: unknown): StayEntitlementAccessMapping {
  const requiredKeys = [
    'status',
    'property_id',
    'room_id',
    'slot_id',
    'topology_revision',
    'access_revision',
    'groups',
  ] as const;
  const optionalKeys = ['scope', 'online_clue_authorization'] as const;
  if (
    !record(value) ||
    !exactWithOptional(value, requiredKeys, optionalKeys) ||
    (value.scope !== undefined && value.scope !== 'CORE_LORA') ||
    (value.online_clue_authorization !== undefined &&
      value.online_clue_authorization !== 'NOT_INCLUDED') ||
    (value.status !== 'OBSERVED' && value.status !== 'UNAVAILABLE') ||
    !nullableUuid(value.property_id) ||
    !nullableUuid(value.room_id) ||
    !isUuid(value.slot_id) ||
    !(value.topology_revision === null || integer(value.topology_revision)) ||
    !(value.access_revision === null || integer(value.access_revision)) ||
    !Array.isArray(value.groups)
  )
    throw invalid();
  return {
    scope: 'CORE_LORA',
    status: value.status,
    property_id: value.property_id,
    room_id: value.room_id,
    slot_id: value.slot_id,
    topology_revision: value.topology_revision,
    access_revision: value.access_revision,
    groups: value.groups.map(parseAccessGroup),
    online_clue_authorization: 'NOT_INCLUDED',
  };
}

function parseEntitlements(value: unknown, expectedStayId: string): StayEntitlements {
  if (
    !record(value) ||
    !exact(value, ['stay_id', 'issues', 'grants', 'access_mapping']) ||
    value.stay_id !== expectedStayId ||
    !Array.isArray(value.issues) ||
    !Array.isArray(value.grants) ||
    value.issues.length > STAY_ENTITLEMENT_RELATED_ROW_BUDGET ||
    value.grants.length > STAY_ENTITLEMENT_RELATED_ROW_BUDGET
  )
    throw invalid();
  return {
    stay_id: value.stay_id,
    issues: value.issues.map(parseIssue),
    grants: value.grants.map(parseGrant),
    access_mapping: parseAccessMapping(value.access_mapping),
  };
}

/** Read bounded issue/grant/revocation snapshots. There is no pagination or write action. */
export async function readStayEntitlements(
  client: ServiceClient,
  stayId: string,
  signal?: AbortSignal,
): Promise<StayEntitlements> {
  if (!isUuid(stayId) || stayId !== stayId.toLowerCase()) throw invalid();
  const value = await client.get<unknown>(`admin/stays/${stayId}/entitlements`, { signal });
  return parseEntitlements(value, stayId);
}
